import { requestIdSchema } from '@baby-tracker/validation';
import { z } from 'zod';

/**
 * Stable, machine-readable error codes. Messages are human-readable and may change;
 * clients should branch on `code`.
 */
export const apiErrorCodeSchema = z.enum([
    'bad_request',
    'validation_failed',
    'unauthenticated',
    'forbidden',
    'not_found',
    'conflict',
    'payload_too_large',
    'rate_limited',
    'internal_error',
]);

export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

export const apiErrorSchema = z.object({
    code: apiErrorCodeSchema,
    message: z.string().min(1),
    /** Optional structured detail, e.g. field-level validation issues. Never contains stack traces. */
    details: z.unknown().optional(),
    requestId: requestIdSchema.optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const errorEnvelopeSchema = z.object({
    error: apiErrorSchema,
});

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

/**
 * Wraps a payload schema in the standard `{ data }` success envelope.
 */
export function successEnvelope<TData extends z.ZodType>(
    data: TData,
): z.ZodObject<{ data: TData }> {
    return z.object({ data });
}

export interface SuccessEnvelope<TData> {
    data: TData;
}

/**
 * Maps error codes to the HTTP status the API uses for them.
 */
export const apiErrorStatus: Record<ApiErrorCode, number> = {
    bad_request: 400,
    validation_failed: 422,
    unauthenticated: 401,
    forbidden: 403,
    not_found: 404,
    conflict: 409,
    payload_too_large: 413,
    rate_limited: 429,
    internal_error: 500,
};
